import React, { useState, useEffect } from "react";
import { X, Sparkles } from "lucide-react";

// Newest first. Bump `id` whenever you ship a release to trigger the pulse for all users.
// This drawer is now dedicated to the Trade Clipping feature set — the flagship
// differentiator vs Tradervue/MorEdge.
export const CHANGELOG = [
  {
    id: "clips-dashboard-pin",
    date: "New",
    title: "Pin your favorite clip to the Dashboard",
    items: [
      "Open Library → click any clip → hit \"Pin to Dashboard\" in the modal header.",
      "Your pinned clip shows at the bottom of the Dashboard with a purple-accented player, symbol, P&L, and duration.",
      "Only one pinned clip at a time — pinning a new one automatically unpins the old.",
      "Pinned clips never auto-expire. They live forever alongside your best setups.",
    ],
  },
  {
    id: "clips-weekly-reel",
    date: "Included",
    title: "Weekly Reel · Star clips → auto-highlight film",
    items: [
      "Star your best trades of the week in the Daily Recap or Library.",
      "Head to Weekly Reel → we auto-concatenate all starred clips into one shareable highlight film.",
      "Client-side FFmpeg re-encodes at 2 Mbps @ 720p max — small enough to text, pretty enough to post.",
      "Perfect for building a track record, coaching juniors, or humbling yourself on tape.",
    ],
  },
  {
    id: "clips-obs-upload",
    date: "Included",
    title: "Massive OBS recording? No problem.",
    items: [
      "Drop your 7GB+ full-session OBS .mkv or .mp4 file into Replay — no shrink required.",
      "Client-side FFmpeg (WORKERFS mount) reads the file directly off your disk — never blows up browser RAM.",
      "Each of your trades gets auto-cut into its own clip using entry/exit timestamps ± your chosen buffer.",
      "Broker timezone alignment fixes the 2-hour drift on brokers that mis-report fills in UTC.",
    ],
  },
  {
    id: "clips-video-review",
    date: "Included",
    title: "Trade-by-trade video review",
    items: [
      "Every clip is linked to the trade it belongs to — jump straight from your Trades table into the tape.",
      "HTTP 206 partial-content streaming = smooth scrubbing/seeking even on 5-min setup clips.",
      "Add trade notes right below the player — build a searchable playbook of what worked.",
      "Fullscreen supported (no ugly black letterbox borders).",
    ],
  },
  {
    id: "clips-library",
    date: "Included",
    title: "Library · Organize by folder or streak",
    items: [
      "Drag-and-drop clips into folders (\"Breakouts\", \"Failed setups\", \"Big wins\"…).",
      "Filter by starred, unfiled, or any custom folder.",
      "Search by symbol, side, or date.",
      "Poster-frame thumbnails so you can visually scan your library.",
    ],
  },
  {
    id: "clips-share",
    date: "Included",
    title: "Share a single clip with a public link",
    items: [
      "Generate a shareable URL for any clip — no login required on the receiving end.",
      "Great for asking mentors for feedback, or showing off a clean scalp in Discord.",
      "Revoke the share link any time and the URL instantly stops working.",
    ],
  },
];

export default function WhatsNew({ open, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-stretch justify-end" data-testid="whats-new-overlay">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md h-full bg-slate-950 border-l border-slate-800 overflow-y-auto" data-testid="whats-new-drawer">
        <div className="sticky top-0 bg-slate-950/95 backdrop-blur border-b border-slate-800 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-emerald-400" strokeWidth={1.5} />
            <div>
              <div className="label-eyebrow text-emerald-400">TRADE CLIPPING</div>
              <div className="text-sm text-zinc-200 font-medium mt-0.5">The killer feature</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="whats-new-close" className="text-zinc-500 hover:text-zinc-100 transition-colors">
            <X className="h-5 w-5" strokeWidth={1.5} />
          </button>
        </div>
        <div className="px-6 py-6 space-y-7">
          {CHANGELOG.map((rel, i) => (
            <div key={rel.id} className="relative pl-5">
              <div className={`absolute left-0 top-1.5 w-2 h-2 rounded-full ${i === 0 ? "bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.7)]" : "bg-zinc-700"}`} />
              <div className="text-[10px] font-mono-num text-zinc-500 tracking-wider">{rel.date}</div>
              <div className="text-sm text-zinc-100 font-medium mt-0.5">{rel.title}</div>
              <ul className="mt-2 space-y-1.5">
                {rel.items.map((it) => (
                  <li key={it} className="text-xs text-zinc-400 leading-relaxed flex gap-2">
                    <span className="text-emerald-400/70 mt-0.5">›</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="text-[10px] font-mono-num text-zinc-600 text-center pt-4 border-t border-slate-800/60">
            RECORD IT · CLIP IT · REVIEW IT · SHARE IT
          </div>
        </div>
      </div>
    </div>
  );
}

const LAST_SEEN_KEY = "edge_whatsnew_last_seen";

/* eslint-disable */
export function useWhatsNew() {
  const [open, setOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const latest = CHANGELOG[0]?.id;

  useEffect(() => {
    const seen = localStorage.getItem(LAST_SEEN_KEY);
    setHasUnread(seen !== latest);
  }, [latest]);

  const openDrawer = () => {
    setOpen(true);
    if (latest) {
      localStorage.setItem(LAST_SEEN_KEY, latest);
      setHasUnread(false);
    }
  };
  return { open, openDrawer, closeDrawer: () => setOpen(false), hasUnread };
}
/* eslint-enable */
